/**
 * Validação do formulário de usuário, compartilhada pelo modal de criar/editar
 * (desktop e mobile). Padrão de UX: o botão de salvar fica SEMPRE habilitado; ao
 * clicar, mostra-se claramente o que falta (resumo + erro por campo) — nunca um
 * botão desabilitado sem explicação. Ver NN/g, GOV.UK, adamsilver.io.
 */
import {
  type AtualizarUsuarioDTO,
  type CargoUnidade,
  MAX_MATRICULA,
  type PapelKey,
  type UsuarioPublico,
} from "@caint/shared";

export type CampoUsuario = "nome" | "matricula" | "senha" | "unidadeId";

export interface DadosUsuarioForm {
  nome: string;
  matricula: string;
  senha: string;
  ehUnidade: boolean;
  unidadeId: string;
}

export interface FaltaUsuario {
  campo: CampoUsuario;
  /** Mensagem curta e acionável ("informe o e-mail de login"). */
  msg: string;
}

const FORMATO_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Forma em que o servidor guarda o login: sem espaço nas pontas, minúsculas. */
export function canonizarEmail(email: string): string {
  return email.trim().toLowerCase();
}

function faltaNoEmail(d: DadosUsuarioForm, ehEdicao: boolean, emailOriginal?: string): string | null {
  const email = d.matricula.trim();
  if (email.length < 3) {
    return "informe o e-mail de login";
  }
  if (email.length > MAX_MATRICULA) {
    return `e-mail com no máximo ${MAX_MATRICULA} caracteres`;
  }
  // Na edição só cobra o formato se o e-mail MUDOU: conta antiga pode ter um
  // login que não é e-mail, e isso não pode travar a edição do nome dela.
  const intocado =
    ehEdicao && emailOriginal !== undefined && canonizarEmail(emailOriginal) === canonizarEmail(email);
  if (!intocado && !FORMATO_EMAIL.test(email)) {
    return "informe um e-mail válido";
  }
  return null;
}

/**
 * O que ainda falta para criar/salvar. Vazio ⇒ pode salvar. `emailOriginal` é o
 * login atual da conta em edição (ver `faltaNoEmail`).
 */
export function faltasUsuario(
  d: DadosUsuarioForm,
  ehEdicao: boolean,
  emailOriginal?: string,
): FaltaUsuario[] {
  const faltas: FaltaUsuario[] = [];
  if (d.nome.trim().length < 2) {
    faltas.push({ campo: "nome", msg: "informe o nome" });
  }
  const email = faltaNoEmail(d, ehEdicao, emailOriginal);
  if (email) {
    faltas.push({ campo: "matricula", msg: email });
  }
  // A senha só é definida na criação — na edição ela tem fluxo próprio.
  if (!ehEdicao && d.senha.length < 8) {
    faltas.push({ campo: "senha", msg: "senha com no mínimo 8 caracteres" });
  }
  if (d.ehUnidade && !d.unidadeId) {
    faltas.push({ campo: "unidadeId", msg: "selecione a unidade vinculada" });
  }
  return faltas;
}

/** Resumo legível do que falta: "o nome e o e-mail" / "a unidade". */
export function resumoFaltas(faltas: FaltaUsuario[]): string {
  const itens = faltas.map((f) => f.msg);
  if (itens.length <= 1) {
    return itens[0] ?? "";
  }
  return `${itens.slice(0, -1).join(", ")} e ${itens[itens.length - 1]}`;
}

/** Valores do formulário de edição, como estão na tela. */
export interface ValoresUsuarioForm {
  nome: string;
  matricula: string;
  papel: PapelKey;
  /** "" ⇒ sem eixo. */
  eixo: string;
  /** "" ⇒ nenhuma. */
  unidadeId: string;
  cargoUnidade: CargoUnidade;
  ativo: boolean;
}

/**
 * Só o que MUDOU em relação à conta original. Mandar o formulário inteiro
 * regravava campos que ninguém tocou (e sobrescrevia o que outro gestor tivesse
 * acabado de alterar). Objeto vazio ⇒ nada a salvar.
 *
 * Vínculo de unidade só existe no papel "unidade": fora dele vira `null`. O eixo
 * de uma conta de unidade não é editável na tela, então não é tocado.
 */
export function patchUsuario(original: UsuarioPublico, v: ValoresUsuarioForm): AtualizarUsuarioDTO {
  const patch: AtualizarUsuarioDTO = {};
  const nome = v.nome.trim();
  if (nome !== original.nome) {
    patch.nome = nome;
  }
  const email = canonizarEmail(v.matricula);
  if (email !== canonizarEmail(original.matricula)) {
    patch.matricula = email;
  }
  if (v.papel !== original.papel) {
    patch.papel = v.papel;
  }
  const ehUnidade = v.papel === "unidade";
  if (!ehUnidade) {
    const eixo = (v.eixo || null) as AtualizarUsuarioDTO["eixo"];
    if (eixo !== (original.eixo ?? null)) {
      patch.eixo = eixo;
    }
  }
  const unidadeId = ehUnidade ? v.unidadeId || null : null;
  if (unidadeId !== (original.unidadeId ?? null)) {
    patch.unidadeId = unidadeId;
  }
  const cargo = ehUnidade ? v.cargoUnidade : null;
  if (cargo !== (original.cargoUnidade ?? null)) {
    patch.cargoUnidade = cargo;
  }
  if (v.ativo !== original.ativo) {
    patch.ativo = v.ativo;
  }
  return patch;
}
